import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { EMPTY } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { TipoError } from '@core/constants/tipo-error';
import { AuditoriaErroresService } from '../auditoria/auditoria-errores.service';
import type { RegistroErrorRequest } from '@core/interfaces/auditoria-errores.interface';

const UNA_EMISION = 1;
const STATUS_HTTP_DESCONOCIDO = 0;
const CODIGO_HTTP_MINIMO_VALIDO = 100;
const UMBRAL_SEVERIDAD_ADVERTENCIA = 300;
const UMBRAL_SEVERIDAD_ERROR = 400;
const UMBRAL_SEVERIDAD_CRITICA = 500;
const CODIGO_ERROR_CADENA = 'ErrorCadena';
const CODIGO_ERROR_APLICACION = 'ErrorAplicacion';
const STATUS_HTTP_ERROR_INTERNO = 500;
const LONGITUD_MAXIMA_MENSAJE_TECNICO = 500;
const INICIO_MENSAJE = 0;

@Injectable()
export class ManejadorErroresService implements ErrorHandler {
  private readonly auditoriaErroresService = inject(AuditoriaErroresService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly esServidor = isPlatformServer(this.platformId);

  public handleError(error: unknown): void {
    if (this.esServidor) {
      return;
    }

    const registro = this.crearRegistroError(error);

    if (!registro) {
      return;
    }

    this.auditoriaErroresService
      .registrarError(registro)
      .pipe(
        take(UNA_EMISION),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  private crearRegistroError(error: unknown): RegistroErrorRequest | null {
    if (error instanceof HttpErrorResponse) {
      return this.crearRegistroDesdeHttp(error);
    }

    if (error instanceof Error) {
      return this.crearRegistroDesdeErrorAplicacion(error);
    }

    if (typeof error === 'string' && error.trim() !== '') {
      return this.crearRegistroDesdeString(error);
    }

    if (error) {
      const mensaje = this.serializar(error) ?? 'Error desconocido';
      return this.crearRegistroDesdeString(mensaje);
    }

    return null;
  }

  private crearRegistroDesdeHttp(error: HttpErrorResponse): RegistroErrorRequest {
    const severidad = this.obtenerSeveridadHttp(error.status);

    return {
      codigoError: this.normalizarCodigoHttp(error.status),
      codigoHttp: error.status || STATUS_HTTP_DESCONOCIDO,
      tipoServicio: error.url ?? TipoError.errorHttp,
      modulo: TipoError.errorHttp,
      severidad,
      mensajeUsuario: error.message,
      mensajeTecnico: this.sanitizarMensajeTecnico(this.serializar(error.error)),
      urlSolicitud: error.url ?? undefined,
      metodoHttp: this.extraerMetodo(error),
      detallesAdicionales: {
        estado: error.statusText || undefined,
        online: this.obtenerEstadoConexion(),
      },
    };
  }

  private crearRegistroDesdeErrorAplicacion(error: Error): RegistroErrorRequest {
    return {
      codigoError: error.name || CODIGO_ERROR_APLICACION,
      codigoHttp: STATUS_HTTP_ERROR_INTERNO,
      tipoServicio: TipoError.errorAplicacion,
      modulo: TipoError.errorAplicacion,
      severidad: 'error',
      mensajeUsuario: error.message,
      mensajeTecnico: this.sanitizarMensajeTecnico(error.stack),
      detallesAdicionales: {
        origen: 'AngularErrorHandler',
      },
    };
  }

  private crearRegistroDesdeString(mensaje: string): RegistroErrorRequest {
    return {
      codigoError: CODIGO_ERROR_CADENA,
      codigoHttp: STATUS_HTTP_ERROR_INTERNO,
      tipoServicio: TipoError.errorAplicacion,
      modulo: TipoError.errorAplicacion,
      severidad: 'error',
      mensajeUsuario: mensaje,
    };
  }

  private normalizarCodigoHttp(status: number): string {
    if (!status || status < CODIGO_HTTP_MINIMO_VALIDO) {
      return 'HTTP_ERROR_DESCONOCIDO';
    }

    return `HTTP_${status}`;
  }

  private obtenerSeveridadHttp(status: number): 'exito' | 'advertencia' | 'error' | 'error_critico' {
    if (status >= UMBRAL_SEVERIDAD_CRITICA) {
      return 'error_critico';
    }

    if (status >= UMBRAL_SEVERIDAD_ERROR) {
      return 'error';
    }

    if (status >= UMBRAL_SEVERIDAD_ADVERTENCIA) {
      return 'advertencia';
    }

    return 'exito';
  }

  private extraerMetodo(error: HttpErrorResponse): string | undefined {
    const metodo = (error as { method?: unknown }).method;
    return typeof metodo === 'string' ? metodo : undefined;
  }

  private obtenerEstadoConexion(): 'online' | 'offline' | 'desconocido' {
    if (typeof navigator === 'undefined') {
      return 'desconocido';
    }

    return navigator.onLine ? 'online' : 'offline';
  }

  private serializar(valor: unknown): string | undefined {
    if (valor === undefined || valor === null) {
      return undefined;
    }

    if (typeof valor === 'string') {
      return valor;
    }

    try {
      return JSON.stringify(valor);
    } catch {
      return undefined;
    }
  }

  private sanitizarMensajeTecnico(mensaje: string | undefined): string | undefined {
    if (!mensaje) {
      return undefined;
    }

    return mensaje.slice(INICIO_MENSAJE, LONGITUD_MAXIMA_MENSAJE_TECNICO);
  }
}
